/*
 * Copyright (c) 2018 - Indigen Solutions
 * Authors:
 *   - Bichoy Messiha <bm@indigen.com>
 * NOTICE: All information contained here in is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Indigen Solutions.
 */
import { push } from "connected-react-router";
import { put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { verboseCall } from ".";
import { services } from "../../services";
import { UserCredentials } from "../../services/Auth.service";
import { Action } from "../../types/Action";
import { RootState } from "../../types/RootState";
import { getErrorFromCode } from "../../utils/errorHelper";
import {
    AUTH_CHANGE_PASSWORD_ACTION,
    AUTH_FORGOT_PASSWORD_ACTION,
    AUTH_SIGN_IN_ACTION,
    AUTH_SIGN_OUT_ACTION,
    AUTH_SIGN_UP_ACTION,
    resetMe,
    setNotification,
    setToken,
    setUser,
    setUserData,
    setLoading,
    startupNotificationsForMe,
} from "../actions";
import { AUTH_END_SU_ACTION, AUTH_SU_ACTION, setPartiallyAuth } from "../actions/auth.actions";

export const getMe = (state: RootState) => state.me;
export const getEmail = (state: RootState) => state.me.user.email;
export const getIsOnline = (state: RootState) => state.context.isOnline;

function* authSuSaga(action) {
    const isOnline = yield select(getIsOnline);
    try {
        if (!isOnline) {
            throw getErrorFromCode("me.offlineSu");
        }
        if (!action.payload.profileCompleted) {
            throw getErrorFromCode("me.suProfileNotCompleted");
        }
        yield put(push("/"));
        yield put(setLoading(true));
        const customToken = yield verboseCall(() => services.api.auth.su(action.payload.id));
        const credentials = yield verboseCall(() => services.auth.suWithCustomToken(customToken));
        const token = yield verboseCall(() => credentials.user.getIdToken());
        yield put(setPartiallyAuth({ token, isSu: true }));
        const user = yield verboseCall(() => services.api.user.getMe());
        yield put(setUser(user));
        const userData = yield verboseCall(() => services.api.userData.getForMe());
        yield put(setUserData(userData));
        yield put(setLoading(false));
    } catch (error) {
        yield put(setNotification({ ...error, variant: "error" }));
    }
}

function* authEndSuSaga(action) {
    yield put(push("/"));
    yield put(setLoading(true));
    const token = yield verboseCall(() => services.auth.endSu());
    yield put(setPartiallyAuth({ token, isSu: false }));
    const user = yield verboseCall(() => services.api.user.getMe());
    yield put(setUser(user));
    const userData = yield verboseCall(() => services.api.userData.getForMe());
    yield put(setUserData(userData));
    yield put(setLoading(false));
}

function* authSignInSaga(action: Action<string, { email: string; password: string }>) {
    try {
        const credentials: UserCredentials = yield verboseCall(() =>
            services.auth.signIn(action.payload.email, action.payload.password)
        );
        const token = yield verboseCall(() => credentials.user.getIdToken());
        yield put(setToken(token));
        const user = yield verboseCall(() => services.api.user.getMe());
        yield put(setUser(user));
        const userData = yield verboseCall(() => services.api.userData.getForMe());
        yield put(setUserData(userData));
        yield put(startupNotificationsForMe());
    } catch (error) {
        yield put(setNotification(getErrorFromCode(error.code)));
    }
}

function* authSignOutSaga() {
    const isOnline = yield select(getIsOnline);
    try {
        if (isOnline) {
            yield verboseCall(() => services.auth.signOut());
            yield put(setToken(null));
            yield put(resetMe());
        } else {
            yield put(setNotification(getErrorFromCode("auth.offlineLogout")));
        }
    } catch (error) {
        yield put(setNotification({ ...error, variant: "error" }));
    }
}

function* authForgotPasswordSaga(action: Action<string, { email: string }>) {
    try {
        yield put(push("/login"));
        yield verboseCall(() => services.auth.sendPasswordResetEmail(action.payload.email));
        yield put(
            setNotification({
                code: "auth.emailSent",
                message: "Un email vous sera envoyé pour créer un nouveau mot de passe",
                variant: "success",
            })
        );
    } catch (error) {
        yield put(setNotification({ ...error, variant: "error" }));
    }
}

function* authChangePasswordSaga(action: Action<string, { currentPassword?: string; newPassword?: string }>) {
    try {
        // TODO @BM change password firebase
        const { currentPassword, newPassword } = action.payload;
        const email = yield select(getEmail);
        const credentials = yield verboseCall(() => services.auth.changePassword(email, currentPassword, newPassword));
        const token = yield verboseCall(() => credentials.user.getIdToken());
        yield put(setToken(token));
        yield put(
            setNotification({ code: "auth.changePassword", message: "Mot de passe modifié", variant: "success" })
        );
    } catch (error) {
        yield put(setNotification({ ...error, variant: "error" }));
    }
}

function* authSignUpSaga(action: Action<string, { email: string; password: string }>) {
    try {
        const isValidated = yield verboseCall(() => services.api.auth.validateEmail(action.payload.email));
        if (!isValidated) {
            throw getErrorFromCode("auth.invalidEmail");
        }
        const credentials = yield verboseCall(() =>
            services.auth.signUp(action.payload.email, action.payload.password)
        );
        const token = yield verboseCall(() => credentials.user.getIdToken());
        yield put(setToken(token));
        const user = yield verboseCall(() => services.api.user.getMe());
        yield put(setUser(user));
        const userData = yield verboseCall(() => services.api.userData.getForMe());
        yield put(setUserData(userData));
        yield put(startupNotificationsForMe());
    } catch (error) {
        yield put(setNotification({ ...error, variant: "error" }));
    }
}

export default function* authSaga() {
    yield takeEvery(AUTH_SU_ACTION, authSuSaga);
    yield takeEvery(AUTH_SIGN_IN_ACTION, authSignInSaga);
    yield takeEvery(AUTH_SIGN_OUT_ACTION, authSignOutSaga);
    yield takeEvery(AUTH_SIGN_UP_ACTION, authSignUpSaga);
    yield takeLatest(AUTH_FORGOT_PASSWORD_ACTION, authForgotPasswordSaga);
    yield takeEvery(AUTH_CHANGE_PASSWORD_ACTION, authChangePasswordSaga);
    yield takeEvery(AUTH_END_SU_ACTION, authEndSuSaga);
}



// WEBPACK FOOTER //
// ./src/store/sagas/auth.saga.ts